import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    CircularProgress,
    Box,
    Typography,
    InputAdornment
} from '@mui/material';
import AddCardIcon from '@mui/icons-material/AddCard';
import { FinanceService } from '../../../api/services/financeService';
import { HotelService } from '../../../api/services/hotelService';
import { SettingsService } from '../../../api/services/settingsService';
import { toast } from '../../../utils/toast';
import { useCurrency, formatCurrency } from '../../../utils/currency';
import { isUaeProperty, DUBAI_DEFAULT_RATES, calculateDubaiItemizedChargeTax } from '../../../utils/dubaiHospitalityTax';

const CATEGORY_MAPPING = {
    'Food & Beverage (F&B)': 'f_and_b',
    'Spa & Wellness': 'spa_service',
    'Extra Bed': 'extra_bed',
    'Laundry Services': 'miscellaneous',
    'Parking Fee': 'miscellaneous',
    'Room Damage Fee': 'miscellaneous',
    'Cancellation Fee': 'cancellation',
    'Other / Miscellaneous': 'miscellaneous'
};

export default function AddFolioChargeModal({ open, onClose, folioId, reservationRoomId, billingMode, foliosList = [], onSuccess, isUae: isUaeProp, tenantId: propTenantId }) {
    const { user, tenants } = useSelector(state => state.auth || { tenants: [] });
    const authData = (() => {
        try {
            return JSON.parse(localStorage.getItem('adminAuth') || '{}');
        } catch (_) {
            return {};
        }
    })();
    const tenantId = propTenantId || user?.tenantId || authData?.tenantId || '1';
    const activeTenant = (tenants || []).find(t => 
        String(t.tenantId || t.id || t._id) === String(tenantId) ||
        String(t.id || t._id) === String(authData.hotelId)
    );
    const hotel = activeTenant || authData?.hotel || authData?.tenant || authData?.property || {};
    const { currency, currencySymbol } = useCurrency();
    const isUae = isUaeProp !== undefined ? isUaeProp : (
                  currency === 'AED' || 
                  hotel.currency === 'AED' || 
                  hotel.country === 'UAE' || 
                  hotel.city === 'Dubai' || 
                  String(hotel.country || '').trim().toUpperCase() === 'UAE' || 
                  String(hotel.city || '').trim().toLowerCase() === 'dubai' || 
                  isUaeProperty(hotel));

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSpecifiedPremises, setIsSpecifiedPremises] = useState(false);
    const [form, setForm] = useState({
        category: '',
        description: '',
        amount: ''
    });
    const [selectedFolioId, setSelectedFolioId] = useState(folioId);
    const [activeTaxes, setActiveTaxes] = useState([]);
    const [taxAmount, setTaxAmount] = useState(0);
    const [taxDetails, setTaxDetails] = useState(null);

    useEffect(() => {
        const fetchHotelData = async () => {
            try {
                const hotelId = activeTenant?.id || activeTenant?._id || authData?.hotelId;
                if (!hotelId) return;
                const hotelResponse = await HotelService.getById(hotelId, tenantId);
                const hotelData = hotelResponse?.data || hotelResponse;
                setIsSpecifiedPremises(Boolean(hotelData?.isSpecifiedPremises));
            } catch (err) {
                console.error("Failed to fetch hotel details:", err);
            }
        };
        if (open) {
            fetchHotelData();
        }
    }, [open, activeTenant, tenantId]);

    useEffect(() => {
        if (open) {
            setForm({
                category: '',
                description: '',
                amount: ''
            });
            setSelectedFolioId(folioId);
            setActiveTaxes([]);
            setTaxAmount(0);
            setTaxDetails(null);
        }
    }, [open, folioId]);

    useEffect(() => {
        const fetchTaxes = async () => {
            if (!open) return;
            if (!form.category) {
                setActiveTaxes([]);
                setTaxAmount(0);
                return;
            }
            try {
                if (isUae) {
                    const result = await SettingsService.getTaxes(tenantId, { noPagination: true });
                    const slabs = Array.isArray(result) ? result : (result.data || result.taxes || []);
                    let activeSlabs = slabs.filter(t => t.status === 'Active');
                    setActiveTaxes(activeSlabs);
                    return;
                }

                let backendCategory = CATEGORY_MAPPING[form.category] || 'miscellaneous';
                if (form.category === 'Food & Beverage (F&B)') {
                    backendCategory = isSpecifiedPremises ? 'f_and_b_specified' : 'f_and_b';
                }
                const result = await SettingsService.getTaxes(tenantId, { serviceType: backendCategory });
                let slabs = Array.isArray(result) ? result : (result.data || []);
                let activeSlabs = slabs.filter(t => t.status === 'Active');
                
                // Statutory fallback if DB slab setting is not found
                if (activeSlabs.length === 0 && form.category === 'Food & Beverage (F&B)') {
                    if (isSpecifiedPremises) {
                        activeSlabs.push({
                            serviceType: 'f_and_b_specified',
                            gstPct: 18,
                            cgstPct: 9,
                            sgstPct: 9,
                            minThreshold: 0,
                            maxThreshold: 999999999,
                            status: 'Active'
                        });
                    } else {
                        activeSlabs.push({
                            serviceType: 'f_and_b',
                            gstPct: 5,
                            cgstPct: 2.5,
                            sgstPct: 2.5,
                            minThreshold: 0,
                            maxThreshold: 999999999,
                            status: 'Active'
                        });
                    }
                }
                
                setActiveTaxes(activeSlabs);
            } catch (err) {
                console.error("Failed to fetch taxes", err);
                if (isUae) {
                    setActiveTaxes([{
                        name: 'UAE Federal VAT',
                        serviceType: 'vat',
                        applicableTo: 'All',
                        rate: DUBAI_DEFAULT_RATES.vatPct,
                        gstPct: DUBAI_DEFAULT_RATES.vatPct,
                        minThreshold: 0,
                        maxThreshold: 999999999,
                        status: 'Active'
                    }]);
                }
            }
        };
        fetchTaxes();
    }, [open, form.category, tenantId, isSpecifiedPremises, isUae]);

    useEffect(() => {
        const amount = parseFloat(form.amount) || 0;
        if (amount <= 0 || activeTaxes.length === 0) {
            setTaxAmount(0);
            return;
        }

        let applicableTax = null;
        for (const tax of activeTaxes) {
            const min = parseFloat(tax.minThreshold || 0);
            const max = parseFloat(tax.maxThreshold || 999999999);
            if (amount >= min && amount <= max) {
                applicableTax = tax;
                break;
            }
        }

        if (applicableTax) {
            const pct = parseFloat(applicableTax.rate !== undefined && applicableTax.rate !== null && applicableTax.rate !== '' ? applicableTax.rate : (applicableTax.gstPct || 0));
            setTaxAmount((amount * pct) / 100);
            setTaxDetails(applicableTax);
        } else {
            setTaxAmount(0);
            setTaxDetails(null);
        }
    }, [form.amount, activeTaxes]);

    const uaeBreakdown = (isUae && parseFloat(form.amount || 0) > 0)
        ? calculateDubaiItemizedChargeTax({ baseAmount: form.amount, category: form.category, taxes: activeTaxes })
        : null;

    const handleTextChange = (field) => (event) => {
        setForm(prev => ({ ...prev, [field]: event.target.value }));
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (!val || val.match(/^\d*\.?\d*$/)) {
            setForm(prev => ({ ...prev, amount: val }));
        }
    };

    const handleSubmit = async () => {
        if (!form.category || !form.description || !form.amount) {
            toast.error('Please fill out all required charge fields.');
            return;
        }

        const amountVal = parseFloat(form.amount);
        if (isNaN(amountVal) || amountVal <= 0) {
            toast.error('Charge amount must be a positive number greater than zero.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                amount: amountVal,
                category: form.category,
                description: form.description
            };

            console.log("Submitting Charge with Payload:", payload, "reservationRoomId query param was:", reservationRoomId);
            await FinanceService.addFolioCharge(selectedFolioId || folioId, payload, reservationRoomId);
            toast.success('New financial charge successfully posted to guest folio balance');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to post manual charge:', error);
            // Error is handled via global interceptor, but we can safely stop loading
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={!isSubmitting ? onClose : undefined} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: '1px solid #eee', bgcolor: '#fbfbfb', display: 'flex', alignItems: 'center' }}>
                <AddCardIcon sx={{ mr: 1, color: 'primary.main' }} /> Post Itemized Fee
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Manually append a discrete line-item fee onto the active transaction folio.
                    </Typography>


                    <FormControl fullWidth sx={{ mb: 3 }} required>
                        <InputLabel>Charge Category</InputLabel>
                        <Select
                            value={form.category}
                            label="Charge Category"
                            onChange={handleTextChange('category')}
                            disabled={isSubmitting}
                        >
                            {Object.keys(CATEGORY_MAPPING).map(cat => (
                                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        required
                        label="Line-Item Description"
                        placeholder="e.g., Room Service Dinner"
                        value={form.description}
                        onChange={handleTextChange('description')}
                        sx={{ mb: 3 }}
                        disabled={isSubmitting}
                    />

                    <TextField
                        fullWidth
                        required
                        label="Charge Amount (Base)"
                        placeholder="0.00"
                        value={form.amount}
                        onChange={handleAmountChange}
                        disabled={isSubmitting}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                        }}
                    />
                    
                    {parseFloat(form.amount || 0) > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">Charge Amount (Base)</Typography>
                                <Typography variant="body2" color="text.primary">{formatCurrency(parseFloat(form.amount || 0), currency)}</Typography>
                            </Box>

                            {isUae && uaeBreakdown ? (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Service Charge ({uaeBreakdown.serviceChargePct}%)
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {uaeBreakdown.isCancellation ? 'Exempt (0.00)' : `+ ${formatCurrency(uaeBreakdown.serviceChargeAmount, currency)}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Dubai Municipality Fee ({uaeBreakdown.municipalityFeePct}%)
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {uaeBreakdown.isCancellation ? 'Exempt (0.00)' : `+ ${formatCurrency(uaeBreakdown.municipalityFeeAmount, currency)}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                UAE VAT ({uaeBreakdown.vatPct}% on {uaeBreakdown.isCancellation ? 'Base' : 'Subtotal'})
                                            </Typography>
                                            {!uaeBreakdown.isCancellation && (
                                                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.68rem' }}>
                                                    Subtotal before VAT: {formatCurrency(uaeBreakdown.subtotalBeforeVat, currency)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.primary">+ {formatCurrency(uaeBreakdown.vatAmount, currency)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">Tourism Dirham</Typography>
                                        <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, mt: 0.25 }}>Exempt (0.00 AED)</Typography>
                                    </Box>
                                </>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Estimated Tax</Typography>
                                        {taxDetails && ((taxDetails.rate !== undefined && taxDetails.rate > 0) || (taxDetails.gstPct > 0)) && (
                                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                                                {`Includes ${parseFloat(taxDetails.cgstPct || 0)}% CGST & ${parseFloat(taxDetails.sgstPct || 0)}% SGST`}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.primary" sx={{ mt: 0.25 }}>+ {formatCurrency(taxAmount, currency)}</Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, pt: 1.5, borderTop: '1px dashed #cbd5e1' }}>
                                <Typography variant="subtitle2" fontWeight="bold">Total Post Amount</Typography>
                                <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
                                    {formatCurrency((isUae && uaeBreakdown) ? uaeBreakdown.totalPostAmount : (parseFloat(form.amount || 0) + taxAmount), currency)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', bgcolor: '#fbfbfb' }}>
                <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ px: 3 }}
                >
                    {isSubmitting ? 'Posting...' : 'Post Charge'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
